import React, { useRef, useState, useCallback, useEffect } from 'react';
import {
  IonCard,
  IonHeader,
  IonIcon,
  IonContent,
  IonPage,
  IonToolbar,
  IonButtons,
  IonTitle,
  IonButton,
  IonCol,
  useIonRouter,
  IonBackButton,
  IonGrid,
  IonRow,
  IonItem,
  IonLabel,
  IonProgressBar,
  IonCardHeader,
  useIonViewDidEnter,
  useIonViewDidLeave,
  useIonViewWillEnter
} from '@ionic/react';
import {
  chevronForward,
  chevronDown,
  listOutline as agendaIcon,
  chevronBack,
} from 'ionicons/icons';
import { TbCalendar, TbCalendarMonth } from "react-icons/tb";
import { LuCalendarPlus, LuCalendarX } from "react-icons/lu";
import TodayCalendarIcon from '../../components/TodayCalendarIcon';
import MonthYearPickerModal from './MonthYearPickerModal';
import { motion, useMotionValue, animate, useTransform } from 'motion/react';
import styles from './Calendar.module.scss';
import Agenda from './Agenda';
import { addDays, isSameDay, startOfDay, startOfWeek, addWeeks, differenceInCalendarDays } from 'date-fns';
import { serviceColorMap } from '@rsmaids/shared/types/services';
import { DayBookings, BookingItem } from '@rsmaids/shared/types/bookings';
import ThreeDayView from './ThreeDayView';
import { DataStorageService } from '@rsmaids/shared/services/DataStorageService';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import PortalDropdown from '../../components/PortalDropdown';
import ContactPickerSheet from '../../components/ContactPickerSheet';
import { Contact, ContactListItem } from '@rsmaids/shared/types/contacts';
import {
  getBlockedSlotsFromCache,
  getBookingsFromCache,
  subscribeToBlockedSlots,
  subscribeToUpcomingBookings,
} from '@rsmaids/shared/services/api/bookingsCacheService';
import DayView from './DayView';
import Booking from '../bookingSystem/BookingForm';
import BookingDetails from '../bookingSystem/BookingDetails';
import BlockedTimeForm from './BlockedTimeForm';
import { useTranslation } from 'react-i18next';
import { intlLocale, weekdayNames } from '../../i18n/locale';



async function loadServiceIds(service: { getServiceTypes: () => Promise<Array<{ serviceId?: string }>> }): Promise<string[]> {
  const cachedServiceIds = sessionStorage.getItem('cachedServiceIds');
  if (cachedServiceIds) {
    return JSON.parse(cachedServiceIds) as string[];
  }

  const services = await service.getServiceTypes();
  const serviceIds = services
    .map((s) => s.serviceId)
    .filter((id): id is string => Boolean(id));
  sessionStorage.setItem('cachedServiceIds', JSON.stringify(serviceIds));
  return serviceIds;
}

const MONTH_WEEK_HEIGHT = 28;
const WEEK_VIEW_HEIGHT = 0;
const HEADER_HEIGHT = 40;
const WEEKDAY_HEADER_HEIGHT = 24;
const BASE_HEIGHT = HEADER_HEIGHT + WEEKDAY_HEADER_HEIGHT;
const MIN_HEIGHT = BASE_HEIGHT + WEEK_VIEW_HEIGHT;
const MONTH_GAP = 10;
const CACHE_DURATION = 10 * 60 * 1000;

const Calendar: React.FC = () => {
  const { t: tr } = useTranslation();
  const lastFetchedWindowRef = useRef<{ start: string; end: string } | null>(null);
  const lastLoadedNextEndRef = useRef<string | null>(null);
  const lastLoadedPrevStartRef = useRef<string | null>(null);
  // True when selectedDate was just changed by scrolling the Agenda (not by a tap / swipe)
  const selectionFromAgendaScrollRef = useRef(false);
  const ionRouter = useIonRouter();

  const triggerNavigationSkeletonRef = useRef<(() => void) | null>(null);

  // Windows (a date ±7 days) whose bookings have already been shown this session.
  // Their days stay in bookingsByDay when you navigate away, so coming back needs no
  // loading state/skeleton — the live listener just re-attaches and refreshes them.
  // Key format matches the cacheKey used by the window effect below.
  const loadedWindowKeysRef = useRef(new Set<string>());
  const windowKeyFor = (date: Date) => {
    const start = startOfDay(addDays(date, -7));
    const end = startOfDay(addDays(date, 7));
    return `${start.toDateString()}_${end.toDateString()}`;
  };

  const height = useMotionValue(MIN_HEIGHT);
  const x = useMotionValue(0);
  const xTransform = useTransform(x, (value) => `translateX(calc(-33.333% + ${value}px))`);
  const containerRef = useRef<HTMLIonCardElement>(null);
  const lastExpandedChangeRef = useRef(0);

  const touchState = useRef({
    isDragging: false,
    isNavigating: false,
    isAgendaScrolling: false,
    isNavigationUpdate: false,
    swipe: {
      isActive: false,
      isVertical: false,
      startX: 0,
      startY: 0,
      initialHeight: 0
    }
  });

  const isAutoScrollingRef = useRef(false);

  // Truncated to the 1st of the month, no time-of-day — computed directly
  // in the lazy initializer (not via a mount effect) since it only ever
  // needs to happen once. Re-setting selectedDate to a fresh Date object
  // (same moment, different reference) in an effect instead would force a
  // second render, since Dates are never reference-equal — that made every
  // [selectedDate]-keyed effect (including Agenda's auto-scroll) fire twice
  // on first mount, with the second pass recomputing the scroll target
  // before layout had settled from the first — exactly why only today's
  // initial scroll undershot.
  const [currentMonth, setCurrentMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [selectedDate, setSelectedDate] = useState(new Date());
  const selectedDateRef = useRef(selectedDate);
  useEffect(() => { selectedDateRef.current = selectedDate; }, [selectedDate]);

  const [isExpandedState, setIsExpandedOriginal] = useState(false);
  const [bookingsByDay, setBookingsByDay] = useState<DayBookings[]>([]);
  const [isLoadingBookings, setIsLoadingBookings] = useState(true);
  // date key → up to 3 unique service colors for that day's dot indicators
  const [monthDotData, setMonthDotData] = useState<Map<string, string[]>>(new Map());

  const bookingsCache = useRef(new Map<string, { data: BookingItem[]; timestamp: number }>());

  const setIsExpanded = useCallback((value: boolean) => {
    const now = Date.now();
    if (now - lastExpandedChangeRef.current < 100) {
      return;
    }

    lastExpandedChangeRef.current = now;
    setIsExpandedOriginal(value);
  }, []);

  const isExpanded = isExpandedState;
  const [calendarView, setCalendarView] = useState<string>(() => {
    return localStorage.getItem('calendarView') || 'agenda';
  });
  const [isViewPopoverOpen, setIsViewPopoverOpen] = useState(false);
  const calendarViewTriggerRef = useRef<HTMLIonButtonElement>(null);
  // Month / year picker: opens (full page) from the month button in the date header.
  const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false);
  const monthButtonRef = useRef<HTMLIonButtonElement>(null);
  const openMonthPicker = useCallback(() => setIsMonthPickerOpen(true), []);
  const calendarViewPortalRef = useRef<HTMLDivElement>(null);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
  const [isBlockedFormOpen, setIsBlockedFormOpen] = useState(false);
  const [selectedBlockedBooking, setSelectedBlockedBooking] = useState<BookingItem | null>(null);
  const isBookingModalOpenRef = useRef(false);
  const selectedBookingIdRef = useRef<string | null>(null);
  // Leaving the Schedule page (e.g. to the Home tab) and coming back starts it fresh —
  // today, week view, lists at their default scroll — unless the user left it by opening
  // an item (a booking, its contact...), in which case it is exactly as they left it.
  const hasLeftPageRef = useRef(false);
  const keepStateOnReturnRef = useRef(false);
  // Changing this remounts the Agenda / Day / 3-day views, putting their scroll back to default
  const [viewResetToken, setViewResetToken] = useState(0);
  // Bumped by the Today button so the agenda scrolls back to the top of today even when today is already selected.
  const [agendaTopToken, setAgendaTopToken] = useState(0);

  // Keep refs in sync with state
  useEffect(() => {
    console.log('Calendar: isBookingModalOpen changed to:', isBookingModalOpen);
    isBookingModalOpenRef.current = isBookingModalOpen;
  }, [isBookingModalOpen]);

  useEffect(() => {
    console.log('Calendar: selectedBookingId changed to:', selectedBookingId);
    selectedBookingIdRef.current = selectedBookingId;
  }, [selectedBookingId]);

  const handleThreeDayDateSelection = useCallback((date: Date): void => {
    setSelectedDate(startOfDay(date));
  }, []);

  const getWeeksInMonth = useCallback((date: Date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);

    const firstWeekday = firstDay.getDay();
    const totalDays = lastDay.getDate() + firstWeekday;
    return Math.ceil(totalDays / 7);
  }, []);

  const calculateMonthHeight = useCallback(() => {
    const weeksInMonth = getWeeksInMonth(currentMonth);
    return BASE_HEIGHT + (MONTH_WEEK_HEIGHT * weeksInMonth);
  }, [currentMonth, getWeeksInMonth]);

  const calculateWeekViewHeight = useCallback(() => {
    return BASE_HEIGHT + WEEK_VIEW_HEIGHT;
  }, []);

  const updateHeight = useCallback((expanded: boolean) => {
    const newHeight = expanded ? calculateMonthHeight() : calculateWeekViewHeight();

    animate(height, newHeight, {
      type: "tween",
      duration: 0.3,
      ease: "easeOut"
    });

    setIsExpanded(expanded);
  }, [calculateMonthHeight, calculateWeekViewHeight, height, setIsExpanded]);

  const generateMonthDays = useCallback((date: Date) => {
    const days = [];
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);

    const startPadding = firstDay.getDay();
    const prevMonthLastDay = new Date(date.getFullYear(), date.getMonth(), 0);

    for (let i = startPadding - 1; i >= 0; i--) {
      days.push(addDays(prevMonthLastDay, -i));
    }

    for (let i = 0; i < lastDay.getDate(); i++) {
      days.push(addDays(firstDay, i));
    }

    const endPadding = 7 - ((days.length) % 7);
    if (endPadding < 7) {
      for (let i = 0; i < endPadding; i++) {
        days.push(addDays(lastDay, i + 1));
      }
    }

    return days;
  }, []);

  useEffect(() => {
    if (touchState.current.isDragging) {
      return;
    }

    if (isExpanded) {
      const newHeight = calculateMonthHeight();
      height.set(newHeight);
    }
  }, [currentMonth, isExpanded, calculateMonthHeight, height]);

  // Pure derivation of the displayed/prev/next month day grids. Must be
  // computed from the first render — the previous "adjust state on change"
  // version only ran when currentMonth changed, so the grids were empty
  // (blank expanded calendar) until the user swiped to another month.
  const displayedDays = React.useMemo(() => generateMonthDays(currentMonth), [currentMonth, generateMonthDays]);
  const prevMonth = React.useMemo(
    () => generateMonthDays(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)),
    [currentMonth, generateMonthDays]
  );
  const nextMonth = React.useMemo(
    () => generateMonthDays(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)),
    [currentMonth, generateMonthDays]
  );

  const handleDateSelection = useCallback((date: Date, forceCollapse: boolean = false): void => {
    if (touchState.current.swipe.isActive && !touchState.current.swipe.isVertical) {
      const newDate = startOfDay(date);
      setSelectedDate(newDate);

      const newMonth = new Date(newDate.getFullYear(), newDate.getMonth(), 1);
      if (currentMonth.getMonth() !== newDate.getMonth() ||
        currentMonth.getFullYear() !== newDate.getFullYear()) {
        setCurrentMonth(newMonth);
      }
      return;
    }

    const newDate = startOfDay(date);
    const newMonth = new Date(newDate.getFullYear(), newDate.getMonth(), 1);

    if (isSameDay(selectedDateRef.current, newDate) &&
      currentMonth.getMonth() === newDate.getMonth() &&
      currentMonth.getFullYear() === newDate.getFullYear()) {
      return;
    }

    // Skeleton + loading state only for a window that hasn't been shown yet; one
    // already in bookingsByDay is displayed as-is while the listener re-attaches.
    if (!loadedWindowKeysRef.current.has(windowKeyFor(newDate))) {
      // trigger the skeleton for date clicks
      triggerNavigationSkeletonRef.current?.();

      // Set loading to prevent "no bookings" flash during navigation
      setIsLoadingBookings(true);
    }

    isAutoScrollingRef.current = true;

    setSelectedDate(newDate);
    setCurrentMonth(newMonth);

    if (forceCollapse) {
      updateHeight(false);
    }

    setTimeout(() => {
      isAutoScrollingRef.current = false;
    }, 500);
  }, [updateHeight, currentMonth, triggerNavigationSkeletonRef]);

  const handleDayClick = useCallback((date: Date) => {
    if (touchState.current.swipe.isActive && !touchState.current.swipe.isVertical) return;
    handleDateSelection(date, true);
  }, [handleDateSelection]);

  const handleWeekChange = useCallback((date: Date) => {
    const startOfWeekDate = startOfWeek(date);
    // Trigger skeleton loader for week navigation (not for a week already shown)
    if (!loadedWindowKeysRef.current.has(windowKeyFor(startOfWeekDate))) {
      triggerNavigationSkeletonRef.current?.();
    }
    if (touchState.current.swipe.isActive && !touchState.current.swipe.isVertical) return;
    handleDateSelection(startOfWeekDate, true);
  }, [handleDateSelection]);

  const handleHorizontalScroll = (direction: 'left' | 'right') => {
    isAutoScrollingRef.current = true;
    touchState.current.isNavigating = true;

    // Where this swipe lands — the same date the branches below select — so the
    // skeleton/loading state can be skipped for a window that was already shown
    // (its days are still in bookingsByDay; the listener just re-attaches).
    const swipeTarget = isExpanded
      ? new Date(currentMonth.getFullYear(), currentMonth.getMonth() + (direction === 'left' ? -1 : 1), 1)
      : addWeeks(startOfWeek(selectedDate), direction === 'left' ? -1 : 1);
    if (!loadedWindowKeysRef.current.has(windowKeyFor(swipeTarget))) {
      // Trigger agenda skeleton loader for month/week navigation
      triggerNavigationSkeletonRef.current?.();

      // Set loading to prevent "no bookings" flash during navigation
      setIsLoadingBookings(true);
    }

    const distance = (containerRef.current?.offsetWidth || 0) + MONTH_GAP;
    const moveX = direction === 'left' ? distance : -distance;

    if (isExpanded) {
      const targetYear = currentMonth.getFullYear();
      const targetMonth = currentMonth.getMonth() + (direction === 'left' ? -1 : 1);

      const newMonthDate = new Date(targetYear, targetMonth, 1);
      const firstDayOfMonth = new Date(newMonthDate.getFullYear(), newMonthDate.getMonth(), 1);

      const newMonthWeeks = getWeeksInMonth(newMonthDate);
      const newHeight = BASE_HEIGHT + (MONTH_WEEK_HEIGHT * newMonthWeeks);

      setCurrentMonth(newMonthDate);
      setSelectedDate(firstDayOfMonth);
      height.set(newHeight);

    } else {
      const currentWeekStart = startOfWeek(selectedDate);
      const targetWeekStart = addWeeks(currentWeekStart, direction === 'left' ? -1 : 1);

      setSelectedDate(targetWeekStart);

      const newMonth = new Date(targetWeekStart.getFullYear(), targetWeekStart.getMonth(), 1);
      if (currentMonth.getMonth() !== targetWeekStart.getMonth() ||
        currentMonth.getFullYear() !== targetWeekStart.getFullYear()) {
        setCurrentMonth(newMonth);
      }

      height.set(calculateWeekViewHeight());
    }

    animate(x, moveX, {
      type: "spring",
      stiffness: 300,
      damping: 40,
      duration: 0.3
    }).then(() => {
      x.set(0);

      setTimeout(() => {
        touchState.current.isNavigating = false;
        isAutoScrollingRef.current = false;
      }, 200);
    });
  };


  const isNavButtonTouch = (e: React.TouchEvent): boolean => {
    const target = e.target as HTMLElement;
    return !!(
      target.closest('ion-button') ||
      target.closest('.ion-button') ||
      target.closest('ion-buttons') ||
      target.closest('[slot="start"]') ||
      target.closest('[slot="end"]') ||
      target.closest('ion-icon') ||
      target.closest('.ion-icon') ||
      target.closest('ion-toolbar') ||
      target.closest('.calendarNavHeader')
    );
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (isNavButtonTouch(e)) {
      return;
    }

    const agendaElement = e.currentTarget.closest('.agendaContent');
    if (agendaElement) {
      touchState.current.isAgendaScrolling = true;
      return;
    }

    touchState.current.swipe.startX = e.touches[0].clientX;
    touchState.current.swipe.startY = e.touches[0].clientY;
    touchState.current.swipe.initialHeight = height.get();
    touchState.current.isDragging = true;
    touchState.current.swipe.isActive = false;
    touchState.current.swipe.isVertical = false;
    touchState.current.isAgendaScrolling = false;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isNavButtonTouch(e)) {
      return;
    }

    if (touchState.current.isAgendaScrolling || !touchState.current.isDragging) return;

    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const diffX = currentX - touchState.current.swipe.startX;
    const diffY = currentY - touchState.current.swipe.startY;

    if (!touchState.current.swipe.isActive && (Math.abs(diffX) > 5 || Math.abs(diffY) > 5)) {
      touchState.current.swipe.isActive = true;
      touchState.current.swipe.isVertical = Math.abs(diffY) > Math.abs(diffX);

      if (touchState.current.swipe.isVertical && diffY > 0 && !isExpanded) {
        setIsExpanded(true);

        setTimeout(() => {
          const monthHeight = calculateMonthHeight();
          height.set(monthHeight);
        }, 0);
      }
    }

    if (touchState.current.swipe.isActive) {
      if (touchState.current.swipe.isVertical) {
        const maxHeight = calculateMonthHeight();
        const minHeight = calculateWeekViewHeight();
        const newHeight = Math.max(minHeight, Math.min(maxHeight, touchState.current.swipe.initialHeight + diffY));

        height.set(newHeight);
      } else {
        x.set(diffX);
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (isNavButtonTouch(e)) {
      return;
    }

    if (!touchState.current.isDragging) return;

    const currentX = e.changedTouches[0].clientX;
    const currentY = e.changedTouches[0].clientY;
    const diffX = currentX - touchState.current.swipe.startX;
    const diffY = currentY - touchState.current.swipe.startY;

    if (Math.abs(diffY) > Math.abs(diffX)) {
      const currentHeight = height.get();
      const monthHeight = calculateMonthHeight();
      const weekHeight = calculateWeekViewHeight();
      const threshold = weekHeight + ((monthHeight - weekHeight) / 2);

      const shouldBeExpanded = currentHeight > threshold;

      setIsExpanded(shouldBeExpanded);

      animate(height, shouldBeExpanded ? monthHeight : weekHeight, {
        type: "spring",
        stiffness: 200,
        damping: 20
      });
    } else {
      const threshold = 50;
      if (Math.abs(diffX) > threshold) {
        handleHorizontalScroll(diffX > 0 ? 'left' : 'right');
      } else {
        x.set(0);
      }
    }

    touchState.current.isDragging = false;
    touchState.current.swipe.isActive = false;
  };

  const handleVerticalDragStart = (e: React.TouchEvent) => {
    if (isNavButtonTouch(e)) {
      return;
    }

    touchState.current.swipe.startY = e.touches[0].clientY;
    touchState.current.swipe.initialHeight = height.get();
    touchState.current.isDragging = true;
  };

  const handleVerticalDragMove = (e: React.TouchEvent) => {
    if (!touchState.current.isDragging || touchState.current.isNavigating) return;

    const currentY = e.touches[0].clientY;
    const diff = currentY - touchState.current.swipe.startY;

    const userIsDraggingDown = diff > 0 && !isExpanded;

    const maxHeight = calculateMonthHeight();
    const minHeight = calculateWeekViewHeight();
    const newHeight = Math.max(minHeight, Math.min(maxHeight, touchState.current.swipe.initialHeight + diff));

    if (userIsDraggingDown) {
      animate(height, newHeight, {
        type: "spring",
        stiffness: 200,
        damping: 20
      });

      if (diff > 5) {
        setTimeout(() => setIsExpanded(true), 0);
      }
    } else {
      animate(height, newHeight, {
        type: "spring",
        stiffness: 200,
        damping: 20
      });
    }
  };

  const handleVerticalDragEnd = () => {
    if (!touchState.current.isDragging || touchState.current.isNavigating) return;

    const currentHeight = height.get();
    const monthHeight = calculateMonthHeight();
    const weekHeight = calculateWeekViewHeight();
    const threshold = weekHeight + ((monthHeight - weekHeight) / 2);
    const shouldBeExpanded = currentHeight > threshold;

    setIsExpanded(shouldBeExpanded);

    animate(height, shouldBeExpanded ? monthHeight : weekHeight, {
      type: "spring",
      stiffness: 200,
      damping: 20
    });

    touchState.current.isDragging = false;
  };

  const handleViewChange = useCallback((newView: string) => {
    setCalendarView(newView);
    localStorage.setItem('calendarView', newView);
  }, []);

  // The month name in the date header — a button that opens the month / year picker.
  const renderMonthButton = () => (
    <IonButton
      ref={monthButtonRef}
      fill="clear"
      className={styles.monthButton}
      aria-label={tr('pages.bookingCalendar.Calendar.chooseMonthAndYear')}
      onClick={openMonthPicker}
    >
      {currentMonth.toLocaleDateString(intlLocale(), { month: 'long', year: 'numeric' })}
      <IonIcon icon={chevronDown} slot="end" />
    </IonButton>
  );

  // Header icon sizes in px. Set as props (not CSS) so the stylesheet can't affect
  // them. The today icon is drawn in Tabler's calendar geometry (TodayCalendarIcon),
  // so it and TbCalendar / TbCalendarMonth are the same shape — one size for all
  // three keeps them equal. Ionicons fill more of their box, so the Agenda IonIcon
  // gets a slightly smaller one to look the same.
  const HEADER_ICON_SIZE = 30;
  const HEADER_ION_ICON_SIZE = 28;
  // Inline style (not just the size prop) so the sizes can't be overridden.
  const tbIconStyle = { width: HEADER_ICON_SIZE, height: HEADER_ICON_SIZE } as const;

  // Header trigger icon for the current view. Day uses react-icons' TbCalendar,
  // which IonIcon can't render (it takes an icon string), so it's returned as an
  // element; the other views stay Ionicons.
  const renderViewIcon = () => {
    switch (calendarView) {
      case '3day':
        return <TbCalendarMonth size={HEADER_ICON_SIZE} className={styles.viewTriggerIcon} style={tbIconStyle} />;
      case 'day':
        return <TbCalendar size={HEADER_ICON_SIZE} className={styles.viewTriggerIcon} style={tbIconStyle} />;
      case 'agenda':
      default:
        return <IonIcon icon={agendaIcon} style={{ fontSize: HEADER_ION_ICON_SIZE }} />;
    }
  };

  function getDateKey(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  const datesWithBookings = React.useMemo(() => {
    // Start with monthDotData (full 90-day load)
    const map = new Map(monthDotData);

    // For days covered by the live agenda snapshot, use bookingsByDay as the
    // authoritative source — it reflects real-time Firestore changes.
    // Only override monthDotData when bookingsByDay actually has something to
    // show for that day — an empty day here can mean a fetch gap (Firestore
    // mirror miss, still-loading window) rather than a genuinely empty day,
    // and unconditionally deleting the dot first threw away correct
    // monthDotData whenever that happened.
    bookingsByDay.forEach(day => {
      const key = getDateKey(day.date);
      const activeItems = day.items.filter(b => {
        const s = String(b.status).toLowerCase();
        return s !== 'canceled' && s !== 'cancelled';
      });
      if (activeItems.length === 0) return;
      const colors: string[] = [];
      activeItems.forEach(b => {
        const color = serviceColorMap[b.serviceType as keyof typeof serviceColorMap] ?? serviceColorMap['Other'];
        if (!colors.includes(color) && colors.length < 3) colors.push(color);
      });
      map.set(key, colors);
    });
    return map;
  }, [monthDotData, bookingsByDay]);

  const threeDayBookings = React.useMemo(() => {
    const rangeStart = startOfDay(selectedDate);
    const rangeEnd = addDays(rangeStart, 3);

    return bookingsByDay
      .flatMap(day => day.items)
      .filter((booking) => {
        const bookingStart = booking.startTime instanceof Date
          ? booking.startTime
          : new Date(booking.startTime);

        return bookingStart >= rangeStart && bookingStart < rangeEnd;
      });
  }, [bookingsByDay, selectedDate]);

  function renderMonth(days: Date[], monthDate: Date) {
    return (
      <IonGrid className={styles.monthView} fixed={true} key={`month-${selectedDate.getTime()}-${monthDate.getTime()}`}>
        <IonRow className={styles.daysGrid}>
          {days.map((date) => {
            const isSelectedDay = isSameDay(selectedDate, date);
            const isToday = isSameDay(new Date(), date);
            const isInactiveMonth = date.getMonth() !== monthDate.getMonth();

            const dots = datesWithBookings.get(getDateKey(date));
            return (
              <IonCol
                key={date.getTime()}
                className={`${styles.dayCell} ${isSelectedDay ? styles.selected : ''}
                  ${isToday ? styles.today : ''}
                  ${isInactiveMonth ? styles.inactiveDay : ''}
                  ${dots ? styles.hasBookings : ''}`}
                onClick={() => { handleDayClick(date); }}
              >
                <span>{date.getDate()}</span>
                {dots && (
                  <div className={styles.dotRow}>
                    {dots.map((color, i) => (
                      <span key={i} className={styles.serviceDot} style={{ backgroundColor: color }} />
                    ))}
                  </div>
                )}
              </IonCol>
            );
          })}
        </IonRow>
      </IonGrid>
    );
  }

  function renderWeekView(date: Date) {
    const weekStart = startOfWeek(date);
    return (
      <IonGrid className={styles.monthView} fixed={true} key={`week-${selectedDate.getTime()}-${date.getTime()}`}>
        <IonRow className={styles.daysGrid}>
          {Array.from({ length: 7 }).map((_, i) => {
            const dayDate = addDays(weekStart, i);
            const isSelectedDay = isSameDay(selectedDate, dayDate);
            const isToday = isSameDay(new Date(), dayDate);
            const isInactiveMonth = dayDate.getMonth() !== currentMonth.getMonth();
            const dots = datesWithBookings.get(getDateKey(dayDate));

            return (
              <IonCol
                key={dayDate.getTime()}
                className={`${styles.dayCell} ${isSelectedDay ? styles.selected : ''}
                  ${isToday ? styles.today : ''}
                  ${isInactiveMonth ? styles.inactiveDay : ''}
                  ${dots ? styles.hasBookings : ''}`}
                onClick={() => { handleDayClick(dayDate); }}
              >
                <span>{dayDate.getDate()}</span>
                {dots && (
                  <div className={styles.dotRow}>
                    {dots.map((color, i) => (
                      <span key={i} className={styles.serviceDot} style={{ backgroundColor: color }} />
                    ))}
                  </div>
                )}
              </IonCol>
            );
          })}
        </IonRow>
      </IonGrid>
    );
  }

  const [completionStatuses, setCompletionStatuses] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const loadCompletionStatuses = () => {
      const statuses: Record<string, boolean> = {};

      if (bookingsByDay && bookingsByDay.length > 0) {
        bookingsByDay.forEach(day => {
          day.items.forEach((booking: BookingItem) => {
            if (booking.id) {
              const status = DataStorageService.getData<boolean>(`booking-${booking.id}-completed`);
              if (status !== null) {
                statuses[booking.id] = status;
              }
            }
          });
        });
      }

      setCompletionStatuses(statuses);
    };

    loadCompletionStatuses();

    // Storage listener - only react to specific triggers
    // 'booking-refresh-trigger' used to force a full agenda reload here —
    // removed in favor of trusting the live Firestore listener
    // (subscribeToUpcomingBookings/subscribeToBlockedSlots), same as Desktop.
    const unsubscribe = DataStorageService.setupStorageListener((key: string) => {
      // Only log and process specific keys to avoid spam from scroll-related storage
      if (key.startsWith('booking-') && key.endsWith('-completed')) {
        // Handle completion status updates silently
        const bookingId = key.replace('booking-', '').replace('-completed', '');
        const updatedStatus = DataStorageService.getData<boolean>(key);
        if (updatedStatus !== null) {
          setCompletionStatuses(prev => ({ ...prev, [bookingId]: updatedStatus }));
        }
      }
      // Ignore all other storage events (like calendarShouldRefresh, scroll position, etc.)
    });

    return () => {
      unsubscribe();
    };
  }, [bookingsByDay]);

  const updateBookingStatus = useCallback((bookingId: string, isCompleted: boolean) => {
    DataStorageService.storeData(`booking-${bookingId}-completed`, isCompleted);

    setCompletionStatuses(prev => ({
      ...prev,
      [bookingId]: isCompleted
    }));
  }, []);

  const handleAddBooking = () => {
    DataStorageService.storeData('bookingSelectedDate', selectedDate.toISOString());

    setIsContactModalOpen(true);
  };

  const handleAddBlockedTime = () => {
    setSelectedBlockedBooking(null);
    setIsBlockedFormOpen(true);
  };

  const handleBookingSelect = useCallback((booking: BookingItem): void => {
    if (booking.type === 'blocked') {
      setSelectedBlockedBooking(booking);
      setIsBlockedFormOpen(true);
      return;
    }

    DataStorageService.storeData('selectedBooking', booking, false);

    setSelectedBookingId(booking.id);
  }, []);

  const handleBlockedFormClose = useCallback(() => {
    setIsBlockedFormOpen(false);
    setSelectedBlockedBooking(null);
  }, []);

  // No manual refresh needed — the live subscribeToBlockedSlots listener
  // (see fetchBookingsForWindow) picks up the change automatically, same as Desktop.
  const handleBlockedFormSaved = useCallback(() => { }, []);

  const handleContactSelect = (contact: ContactListItem) => {
    const address = [
      contact.primaryEmail,
      contact.primaryPhone,
      contact.company
    ].filter(Boolean).join(', ');

    const contactData = {
      ...contact,
      name: contact.displayName || `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim(),
      email: contact.primaryEmail ?? '',
      phone: contact.primaryPhone ?? '',
      address
    };

    DataStorageService.storeData('selectedContact', contactData);
    setIsContactModalOpen(false);
    setIsBookingModalOpen(true);
  };

  useIonViewDidEnter(() => {
    const selectedContact = DataStorageService.getData<Contact>('selectedContact');
    if (selectedContact) {
      DataStorageService.removeData('selectedContact');
    }
  });

  // The reset happens as the page is LEFT (while it is hidden), so it is already back at
  // today / default scroll when the user returns — never visibly resetting on arrival.
  const onPageLeft = () => {
    if (hasLeftPageRef.current) return; // already handled (lifecycle event + observer both fire)
    hasLeftPageRef.current = true;

    if (selectedBookingIdRef.current || isBookingModalOpenRef.current || isBlockedFormOpen) {
      keepStateOnReturnRef.current = true;
    }
    if (keepStateOnReturnRef.current) return; // left via an item: keep everything as it is

    // Deferred: resetting remounts the agenda / day views and refetches today's bookings,
    // and doing that in the middle of the tab switch made the switch itself lag. Waiting
    // until the switch is done lets the other tab appear first.
    setTimeout(() => {
      const newDate = startOfDay(new Date());
      isAutoScrollingRef.current = true;
      setSelectedDate(newDate);
      setCurrentMonth(new Date(newDate.getFullYear(), newDate.getMonth(), 1));
      updateHeight(false);
      setViewResetToken(t => t + 1);
      setTimeout(() => {
        isAutoScrollingRef.current = false;
      }, 500);
    }, 600);
  };

  const onPageReturned = () => {
    if (!hasLeftPageRef.current) return;
    hasLeftPageRef.current = false;
    keepStateOnReturnRef.current = false;
  };

  // Ionic's page lifecycle events…
  useIonViewDidLeave(onPageLeft);

  // …and, in case they don't fire for this page (it stays mounted when you switch tabs, and
  // the reset did nothing), also watch the page element itself: Ionic hides a page that is
  // no longer the active one with the `ion-page-hidden` class. Both paths are idempotent.
  const pageLifecycleRef = useRef({ left: onPageLeft, returned: onPageReturned });
  useEffect(() => {
    pageLifecycleRef.current = { left: onPageLeft, returned: onPageReturned };
  });
  useEffect(() => {
    const page = document.querySelector('.ion-page[data-pageid="tabs-bookingCalendar-Calendar"]');
    if (!page) return;
    let hidden = page.classList.contains('ion-page-hidden');
    const observer = new MutationObserver(() => {
      const nowHidden = page.classList.contains('ion-page-hidden');
      if (nowHidden === hidden) return;
      hidden = nowHidden;
      if (nowHidden) pageLifecycleRef.current.left();
      else pageLifecycleRef.current.returned();
    });
    observer.observe(page, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // The drag strip must be exactly the colour of the page behind it. The page background is a glow that differs from
  // top to bottom, and the strip moves (the calendar expands and collapses), so no fixed colour can match. Instead the
  // strip paints the page's own background (see .dragArea in Calendar.module.scss), sized to the page and shifted by
  // the strip's offset inside it, so the slice of the page that lies under the strip is what it shows.
  useEffect(() => {
    const page = document.querySelector<HTMLElement>('.ion-page[data-pageid="tabs-bookingCalendar-Calendar"]');
    if (!page) return;
    const sync = () => {
      const p = page.getBoundingClientRect();
      page.querySelectorAll<HTMLElement>('[class*="dragArea"]').forEach(strip => {
        const s = strip.getBoundingClientRect();
        strip.style.setProperty('--strip-bg-size', `${p.width}px ${p.height}px`);
        strip.style.setProperty('--strip-bg-pos', `${p.left - s.left}px ${p.top - s.top}px`);
      });
    };
    sync();
    const observer = new ResizeObserver(sync); // fires on every frame the calendar's height changes, too
    observer.observe(page);
    page.querySelectorAll('ion-card[class*="calendar"]').forEach(card => observer.observe(card));
    window.addEventListener('resize', sync);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', sync);
    };
  }, [calendarView]);

  useIonViewWillEnter(() => {
    onPageReturned();

    const calendarPage = document.querySelector('.ion-page[data-pageid="tabs-bookingCalendar-Calendar"]');
    if (calendarPage) {
      calendarPage.classList.remove('ion-page-hidden');

      const stackedPages = document.querySelectorAll('.ion-page:not([data-pageid="tabs-bookingCalendar-Calendar"])');
      stackedPages.forEach(page => {
        if (page.classList.contains('ion-page-visible')) {
          page.classList.remove('ion-page-visible');
          page.classList.add('ion-page-hidden');
        }
      });
    }
  });

  function groupBookingsByDay(bookings: BookingItem[], start: Date, end: Date): DayBookings[] {
    const days: DayBookings[] = [];
    let d = new Date(start);
    while (d <= end) {
      const dayStart = startOfDay(d);
      const dayEnd = new Date(dayStart);
      dayEnd.setHours(23, 59, 59, 999);
      const items = bookings.filter(b => b.startTime >= dayStart && b.startTime <= dayEnd);
      days.push({ date: new Date(dayStart), items });
      d = addDays(d, 1);
    }
    return days;
  }

  // Replaces only the days within [start, end] in bookingsByDay, preserving
  // everything outside that range. The primary window fetch re-centers on
  // selectedDate as the user scrolls (via handleUserScroll's onDateChange),
  // and a plain setBookingsByDay(groupBookingsByDay(...)) there was wiping
  // out every day loadNext7Days had appended beyond the current window —
  // confirmed by bookings counts dropping suddenly (e.g. 34→10) instead of
  // only ever growing.
  function mergeWindowIntoBookingsByDay(start: Date, end: Date, windowDays: DayBookings[], _source: string) {
    setBookingsByDay(prev => {
      const outside = prev.filter(day => day.date < start || day.date > end);
      const result = [...outside, ...windowDays].sort((a, b) => a.date.getTime() - b.date.getTime());
      return result;
    });
  }

  const lastProcessedDateRef = useRef<string | null>(null);
  const firestoreUnsubRef = useRef<(() => void) | null>(null);
  const reconciledWindowsRef = useRef(new Set<string>());
  // The live window listener outlives individual runs of the selectedDate
  // effect (it must keep delivering updates while the user moves around inside
  // the window), so its callbacks can't be guarded by that effect's own
  // `mounted` flag. This is the component-lifetime guard instead.
  const unmountedRef = useRef(false);
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      firestoreUnsubRef.current?.();
      firestoreUnsubRef.current = null;
    };
  }, []);

  const addToDotMap = useCallback((map: Map<string, string[]>, bookings: BookingItem[]) => {
    bookings.forEach(b => {
      const s = String(b.status).toLowerCase();
      if (s === 'cancelled' || s === 'canceled') return;
      const key = b.date || getDateKey(b.startTime instanceof Date ? b.startTime : new Date(b.startTime));
      if (!key) return;
      const color = serviceColorMap[b.serviceType as keyof typeof serviceColorMap] ?? serviceColorMap['Other'];
      const existing = map.get(key) ?? [];
      if (!existing.includes(color) && existing.length < 3) map.set(key, [...existing, color]);
    });
  }, []);

  // Live subscription for dots across the full 90-day Firestore window
  // subscribeToUpcomingBookings fires from server (not stale IndexedDB) ensuring complete dot data
  //
  // Same full range as always, but it starts AFTER the agenda has shown its first
  // data. Firestore works through reads one at a time, and this one is large (~277
  // docs, ~2s to evaluate on the phone): started first (it is registered before
  // the agenda's own window listener) it made the agenda wait behind it —
  // measured: window data arrived with this listener's cache snapshot, +2.4s,
  // versus 0.3-0.6s when it wasn't ahead in the queue.
  const [dotsEnabled, setDotsEnabled] = useState(false);
  if (!dotsEnabled && !isLoadingBookings) setDotsEnabled(true);
  useEffect(() => {
    if (!dotsEnabled) return;
    const today = new Date();
    // Past bounded at 90 days so Firestore doesn't accumulate old history
    // indefinitely; future is effectively unbounded (10 years) — most future
    // bookings won't clutter Firestore, and capping the forward window meant
    // bookings made far enough in advance never got mirrored at all (see
    // onWixBookingWebhook.ts's isInWindow).
    const fsStart = new Date(today); fsStart.setDate(today.getDate() - 90);
    const fsEnd = new Date(today); fsEnd.setDate(today.getDate() + 3650);
    const unsub = subscribeToUpcomingBookings(fsStart, fsEnd, (bookings) => {
      const map = new Map<string, string[]>();
      addToDotMap(map, bookings);
      setMonthDotData(map);
    });
    return unsub;
  }, [addToDotMap, dotsEnabled]);

  // When navigating to a month outside the 90-day Firestore window: load via Wix API
  useEffect(() => {
    const today = new Date();
    // Past bounded at 90 days so Firestore doesn't accumulate old history
    // indefinitely; future is effectively unbounded (10 years) — most future
    // bookings won't clutter Firestore, and capping the forward window meant
    // bookings made far enough in advance never got mirrored at all (see
    // onWixBookingWebhook.ts's isInWindow).
    const fsStart = new Date(today); fsStart.setDate(today.getDate() - 90);
    const fsEnd = new Date(today); fsEnd.setDate(today.getDate() + 3650);
    const monthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0, 23, 59, 59, 999);
    if (monthStart >= fsStart && monthEnd <= fsEnd) return; // covered by the 90-day load

    const queryStart = new Date(monthStart); queryStart.setDate(monthStart.getDate() - 7);
    const queryEnd = new Date(monthEnd); queryEnd.setDate(monthEnd.getDate() + 7);
    let cancelled = false;
    DataServiceFactory.getService().bookings
      .getBookingsByDateRange(queryStart, queryEnd)
      .then(bookings => {
        if (cancelled) return;
        setMonthDotData(prev => {
          const map = new Map(prev);
          addToDotMap(map, bookings);
          return map;
        });
      })
      .catch(() => { });
    return () => { cancelled = true; };
  }, [currentMonth, addToDotMap]);

  useEffect(() => {
    let mounted = true;

    const fetchWithTimeout = (promise: Promise<BookingItem[]>, timeoutMs: number): Promise<BookingItem[]> => {
      return Promise.race([
        promise,
        new Promise<BookingItem[]>((_, reject) =>
          setTimeout(() => reject(new Error('Request timeout')), timeoutMs)
        )
      ]);
    };

    const changedByAgendaScroll = selectionFromAgendaScrollRef.current;
    selectionFromAgendaScrollRef.current = false;

    async function fetchBookingsForWindow() {
      try {
        if (mounted && !changedByAgendaScroll && !loadedWindowKeysRef.current.has(windowKeyFor(selectedDate))) setIsLoadingBookings(true);

        const selectedDateStr = selectedDate.toDateString();

        if (lastProcessedDateRef.current === selectedDateStr) {
          setIsLoadingBookings(false);
          return;
        }
        lastProcessedDateRef.current = selectedDateStr;

        const windowStart = startOfDay(addDays(selectedDate, -7));
        const windowEnd = startOfDay(addDays(selectedDate, 7));
        windowStart.setHours(0, 0, 0, 0);
        windowEnd.setHours(23, 59, 59, 999);

        // Within the 90-day Firestore window (-45/+45 from today): use Firestore
        // live subscriptions for both bookings and blocked slots.
        const today = new Date();
        // Past bounded at 90 days, future effectively unbounded (10 years) — see fetchBookingsForWindow's top-level comment.
        const fsWindowStart = new Date(today); fsWindowStart.setDate(today.getDate() - 90);
        const fsWindowEnd = new Date(today); fsWindowEnd.setDate(today.getDate() + 3650);
        const withinFirestoreWindow = windowStart >= fsWindowStart && windowEnd <= fsWindowEnd;

        // If selectedDate is still inside the window we already fetched/
        // subscribed to, there's nothing to do — the live subscription
        // established on that earlier run is still active and still delivering
        // real-time updates for it. This applies regardless of Firestore vs Wix
        // — without it, every single-day advance from scrolling (which nudges
        // selectedDate day-by-day via handleUserScroll's onDateChange) tore
        // down and rebuilt the entire window subscription, refetching and
        // regrouping bookingsByDay on every scroll tick.
        const currentWindowStart = lastFetchedWindowRef.current?.start;
        const currentWindowEnd = lastFetchedWindowRef.current?.end;

        if (currentWindowStart && currentWindowEnd) {
          const currentStart = new Date(currentWindowStart);
          const currentEnd = new Date(currentWindowEnd);
          if (selectedDate >= currentStart && selectedDate <= currentEnd) {
            setIsLoadingBookings(false);
            return;
          }
        }

        // A genuinely new window is needed: replace the old live listener now
        // (this used to happen in the effect cleanup on EVERY date change,
        // which killed the listener and then the early return above skipped
        // re-creating it — so after any click/scroll mobile stopped receiving
        // live updates until a manual refresh).
        firestoreUnsubRef.current?.();
        firestoreUnsubRef.current = null;

        const cacheKey = `${windowStart.toDateString()}_${windowEnd.toDateString()}`;
        const now = Date.now();

        if (!withinFirestoreWindow) {
          const cached = bookingsCache.current.get(cacheKey);
          if (cached && (now - cached.timestamp) < CACHE_DURATION) {
            if (mounted) {
              const grouped = groupBookingsByDay(cached.data, windowStart, windowEnd);
              mergeWindowIntoBookingsByDay(windowStart, windowEnd, grouped, 'cache-hit-wix-fallback');
              setIsLoadingBookings(false);
              lastFetchedWindowRef.current = { start: windowStart.toDateString(), end: windowEnd.toDateString() };
            }
            return;
          }
        }

        if (withinFirestoreWindow) {
          let cancelled = false;
          let latestBookings: BookingItem[] = [];
          let latestBlocked: BookingItem[] = [];
          let bookingsReady = false;
          let blockedReady = false;
          let wixFallbackChecked = false;
          let firstShownLogged = false;
          // Items Firestore's live subscription didn't have but Wix reconciliation
          // found. Must be re-merged into EVERY publishMerged call, not just the
          // one that triggered reconciliation — otherwise a later Firestore
          // snapshot (e.g. its own cache-then-server delivery) overwrites
          // bookingsByDay with just its own data again, silently dropping
          // whatever reconciliation added back out.
          let missingFromFirestore: BookingItem[] = [];

          const publishMerged = () => {
            if (unmountedRef.current || cancelled) return;

            const seen = new Set<string>();
            const merged: BookingItem[] = [];
            [...latestBookings, ...latestBlocked, ...missingFromFirestore].forEach((item) => {
              if (seen.has(item.id)) return;
              seen.add(item.id);
              merged.push(item);
            });

            bookingsCache.current.set(cacheKey, { data: merged, timestamp: Date.now() });
            mergeWindowIntoBookingsByDay(windowStart, windowEnd, groupBookingsByDay(merged, windowStart, windowEnd), `publishMerged(bookingsReady=${bookingsReady},blockedReady=${blockedReady},missingFromFirestore=${missingFromFirestore.length})`);

            // Show the agenda as soon as bookings are in. Blocked slots merge in
            // whenever they arrive: for a window with none cached, that query's
            // first snapshot only comes from the server (~1.5s measured), and
            // gating the whole agenda on it made every week jump that slow.
            if (bookingsReady) {
              loadedWindowKeysRef.current.add(cacheKey);
              if (!firstShownLogged) {
                firstShownLogged = true;
                console.log(`[agenda-timing] agenda first shown +${Math.round(performance.now() - attachedAt)}ms after attaching`);
              }
              setIsLoadingBookings(false);
            }

            if (bookingsReady && blockedReady) {
              setIsLoadingBookings(false);
              lastFetchedWindowRef.current = {
                start: windowStart.toDateString(),
                end: windowEnd.toDateString(),
              };

              // Firestore's mirror can silently miss individual bookings even
              // inside an otherwise-populated window (root-caused: the dot map
              // gets some months' data straight from Wix — see the effect above
              // that fires when a month isn't fully inside the 45-day window —
              // while this list only ever reads Firestore, whose mirror can be
              // missing a given booking's document). Reconcile against Wix once
              // per window — guarded by wixFallbackChecked, and the "already
              // loaded this window" check earlier in this effect keeps this
              // whole subscription from re-running on every scroll tick — so
              // this fires once per genuinely new window, not per render.
              // Once per window per session: coming back to a window (e.g. Today after
              // browsing another week) makes a new subscription, which used to
              // repeat this whole Wix call every time.
              if (!wixFallbackChecked && !reconciledWindowsRef.current.has(cacheKey)) {
                wixFallbackChecked = true;
                reconciledWindowsRef.current.add(cacheKey);
                (async () => {
                  const service = DataServiceFactory.getService();
                  // Bookings + blocked-time, each via their lighter dedicated
                  // call — getBookingsAndBlockedSlotsByDateRange also computes
                  // availability-derived policy blocking, which took ~15s here
                  // for a check that only needs to know what's actually scheduled.
                  // getBlockedTimeEventsOnly is on the concrete ApiDataService
                  // implementation but not (yet) part of the shared
                  // BookingService interface — cast rather than widen the
                  // interface, since DataServiceFactory always returns
                  // ApiDataService for this call path.
                  const bookingsService = service.bookings as typeof service.bookings & {
                    getBlockedTimeEventsOnly: (start: Date, end: Date) => Promise<BookingItem[]>;
                  };
                  const [wixBookings, wixBlocked] = await Promise.all([
                    bookingsService.getBookingsByDateRange(windowStart, windowEnd),
                    bookingsService.getBlockedTimeEventsOnly(windowStart, windowEnd),
                  ]);
                  if (unmountedRef.current || cancelled) return;

                  // A "blocked" entry that time-overlaps a real booking is
                  // almost certainly that booking's own calendar event getting
                  // misclassified (Wix marks a resource "busy" during any
                  // booking, and that busy-marker can slip through the
                  // events-only query's APPOINTMENT-type filter) — not a
                  // genuine user-created block. Drop it rather than let it
                  // override the real booking once merged.
                  const toMs = (t: string | Date) => (t instanceof Date ? t : new Date(t)).getTime();
                  const overlaps = (a: BookingItem, b: BookingItem) =>
                    toMs(a.startTime) < toMs(b.endTime) && toMs(b.startTime) < toMs(a.endTime);
                  const realBookings = [...merged.filter(m => m.type !== 'blocked'), ...wixBookings];
                  const wixBlockedFiltered = wixBlocked.filter(
                    blocked => !realBookings.some(rb => overlaps(rb, blocked))
                  );

                  const missing = [...wixBookings, ...wixBlockedFiltered].filter(wb => !seen.has(wb.id));
                  if (missing.length === 0) return;
                  missingFromFirestore = missing;
                  publishMerged();
                })().catch(() => { });
              }
            }
          };

          const attachedAt = performance.now();
          console.log(`[agenda-timing] agenda attaching window listeners for ${windowStart.toDateString()} → ${windowEnd.toDateString()} (page time ${Math.round(attachedAt)}ms)`);
          const unsubBookings = subscribeToUpcomingBookings(
            windowStart,
            windowEnd,
            (bookings) => {
              latestBookings = bookings;
              bookingsReady = true;
              publishMerged();
            },
            () => {
              bookingsReady = true;
              publishMerged();
            }
          );

          const unsubBlocked = subscribeToBlockedSlots(
            windowStart,
            windowEnd,
            (blocked) => {
              latestBlocked = blocked;
              blockedReady = true;
              publishMerged();
            },
            () => {
              blockedReady = true;
              publishMerged();
            }
          );

          firestoreUnsubRef.current = () => {
            cancelled = true;
            unsubBookings();
            unsubBlocked();
          };
          return;
        }

        // Outside Firestore window — Wix API, bookings and blocked time
        const service = DataServiceFactory.getService();
        const serviceIds = await loadServiceIds(service);
        const bookings = await fetchWithTimeout(
          service.bookings.getBookingsAndBlockedSlotsByDateRange(windowStart, windowEnd, serviceIds),
          10000
        );
        if (mounted) {
          bookingsCache.current.set(cacheKey, { data: bookings, timestamp: now });
          mergeWindowIntoBookingsByDay(windowStart, windowEnd, groupBookingsByDay(bookings, windowStart, windowEnd), 'outside-window-wix-fallback');
          setIsLoadingBookings(false);
          lastFetchedWindowRef.current = { start: windowStart.toDateString(), end: windowEnd.toDateString() };
        }
      } catch {
        if (mounted) setIsLoadingBookings(false);
      }
    }

    fetchBookingsForWindow();
    return () => {
      mounted = false;
      lastProcessedDateRef.current = null;
      lastLoadedNextEndRef.current = null;
      lastLoadedPrevStartRef.current = null;
      // Deliberately NOT unsubscribing here — see the comment where the old
      // listener is replaced; unmount cleanup lives with unmountedRef.
    };
  }, [selectedDate]);

  // One week of bookings + blocked time (cache → Firestore → Wix), shared by
  // loadNext7Days and loadPrevious7Days.
  const fetchFromWixLight = useCallback(async (start: Date, end: Date): Promise<BookingItem[]> => {
    const service = DataServiceFactory.getService();
    const bookingsService = service.bookings as typeof service.bookings & {
      getBlockedTimeEventsOnly: (s: Date, e: Date) => Promise<BookingItem[]>;
    };
    const [wixBookings, wixBlocked] = await Promise.all([
      bookingsService.getBookingsByDateRange(start, end),
      bookingsService.getBlockedTimeEventsOnly(start, end),
    ]);
    return [...wixBookings, ...wixBlocked];
  }, []);

  // `verifyEmptyWithWix` false = Firestore only. The pagination loops below
  // pass false and do ONE Wix check over the whole empty span at the end —
  // otherwise each empty week cost two Wix calls in sequence (up to 8 weeks
  // back to back), which is what made scrolling/initial fill of the agenda crawl.
  const fetchWeekBookings = useCallback(async (start: Date, end: Date, verifyEmptyWithWix: boolean = true): Promise<BookingItem[]> => {
      const cacheKey = `${start.toDateString()}_${end.toDateString()}`;
      const now = Date.now();
      const cached = bookingsCache.current.get(cacheKey);
      if (cached && (now - cached.timestamp) < CACHE_DURATION) {
        return cached.data;
      }

      let weekBookings: BookingItem[] = [];
      const today = new Date();
      // Past bounded at 90 days, future effectively unbounded (10 years) — see fetchBookingsForWindow's top-level comment.
      const fsWindowStart = new Date(today); fsWindowStart.setDate(today.getDate() - 90);
      const fsWindowEnd = new Date(today); fsWindowEnd.setDate(today.getDate() + 3650);
      if (start >= fsWindowStart && end <= fsWindowEnd) {
        const [firestoreBookings, firestoreBlocked] = await Promise.all([
          getBookingsFromCache(start, end),
          getBlockedSlotsFromCache(start, end),
        ]);
        const seen = new Set<string>();
        weekBookings = [];
        [...firestoreBookings, ...firestoreBlocked].forEach((item) => {
          if (seen.has(item.id)) return;
          seen.add(item.id);
          weekBookings.push(item);
        });

        // Firestore's mirror can have gaps — an empty page here isn't proof
        // there's nothing scheduled. Confirm via the lightweight Wix calls
        // (not getBookingsAndBlockedSlotsByDateRange, which also computes
        // availability-derived policy blocking and was the ~15s slowdown
        // when this was tried for the primary window fetch).
        if (weekBookings.length === 0 && verifyEmptyWithWix) {
          weekBookings = await fetchFromWixLight(start, end);
        }
      } else {
        const service = DataServiceFactory.getService();
        const serviceIds = await loadServiceIds(service);
        weekBookings = await service.bookings.getBookingsAndBlockedSlotsByDateRange(start, end, serviceIds);
      }

      bookingsCache.current.set(cacheKey, { data: weekBookings, timestamp: now });
      return weekBookings;
  }, [fetchFromWixLight]);

  // bookingsByDay can hold several separate stretches of days (e.g. today's window plus
  // a window loaded after jumping months back). The Agenda must only see the one
  // unbroken stretch around the selected date, and "load more" must extend THAT
  // stretch — not whatever happens to be last in the array, which can be months away.
  const agendaRun = React.useMemo(() => {
    const idx = bookingsByDay.findIndex(d => isSameDay(d.date, selectedDate));
    if (idx < 0) return bookingsByDay;
    let first = idx;
    let last = idx;
    while (first > 0 && differenceInCalendarDays(bookingsByDay[first].date, bookingsByDay[first - 1].date) === 1) first--;
    while (last < bookingsByDay.length - 1 && differenceInCalendarDays(bookingsByDay[last + 1].date, bookingsByDay[last].date) === 1) last++;
    return bookingsByDay.slice(first, last + 1);
  }, [bookingsByDay, selectedDate]);

  const loadNext7Days = useCallback(async () => {
    if (agendaRun.length === 0) return;

    // Advance from last FETCHED end (not last day with bookings) so empty windows still progress
    const runEnd = agendaRun[agendaRun.length - 1].date;
    let cursorEnd = runEnd;
    if (lastLoadedNextEndRef.current) {
      const remembered = new Date(lastLoadedNextEndRef.current);
      if (remembered > runEnd) cursorEnd = remembered;
    }

    const allNewDays: DayBookings[] = [];
    // A single empty week shouldn't stop pagination — keep pulling weeks
    // until one has something, capped so a genuinely quiet stretch still ends.
    const MAX_EMPTY_WEEKS = 8;
    let foundAny = false;
    for (let i = 0; i < MAX_EMPTY_WEEKS; i++) {
      const nextStart = addDays(startOfDay(cursorEnd), 1);
      const nextEnd = addDays(nextStart, 6);
      nextStart.setHours(0, 0, 0, 0);
      nextEnd.setHours(23, 59, 59, 999);

      const weekBookings = await fetchWeekBookings(nextStart, nextEnd, false);
      allNewDays.push(...groupBookingsByDay(weekBookings, nextStart, nextEnd));
      cursorEnd = nextEnd;

      if (weekBookings.length > 0) { foundAny = true; break; }
    }

    // Everything came back empty from Firestore: confirm the whole span with
    // Wix once (the mirror can have gaps) instead of once per empty week.
    if (!foundAny && allNewDays.length > 0) {
      const spanStart = allNewDays[0].date;
      const spanEnd = cursorEnd;
      const wixItems = await fetchFromWixLight(spanStart, spanEnd).catch(() => [] as BookingItem[]);
      if (wixItems.length > 0) {
        allNewDays.splice(0, allNewDays.length, ...groupBookingsByDay(wixItems, spanStart, spanEnd));
      }
    }

    lastLoadedNextEndRef.current = cursorEnd.toDateString();
    setBookingsByDay(prev => {
      // keep it sorted and free of duplicate days (a loaded week can overlap another stretch)
      const have = new Set(prev.map(d => d.date.toDateString()));
      const fresh = allNewDays.filter(d => !have.has(d.date.toDateString()));
      return [...prev, ...fresh].sort((a, b) => a.date.getTime() - b.date.getTime());
    });
  }, [agendaRun, fetchWeekBookings, fetchFromWixLight]);

  // Mirror of loadNext7Days going backwards, so the Agenda can scroll up past the
  // start of the current week. Stops a year back.
  const loadPrevious7Days = useCallback(async () => {
    if (agendaRun.length === 0) return;

    const floor = addDays(startOfDay(new Date()), -365);
    let cursorStart = startOfDay(agendaRun[0].date);
    if (lastLoadedPrevStartRef.current) {
      const remembered = startOfDay(new Date(lastLoadedPrevStartRef.current));
      if (remembered < cursorStart) cursorStart = remembered;
    }
    if (cursorStart <= floor) return;

    const allNewDays: DayBookings[] = [];
    const MAX_EMPTY_WEEKS = 8;
    let foundAny = false;
    for (let i = 0; i < MAX_EMPTY_WEEKS && cursorStart > floor; i++) {
      const prevEnd = addDays(cursorStart, -1);
      const prevStart = addDays(prevEnd, -6);
      prevStart.setHours(0, 0, 0, 0);
      prevEnd.setHours(23, 59, 59, 999);

      const weekBookings = await fetchWeekBookings(prevStart, prevEnd, false);
      allNewDays.unshift(...groupBookingsByDay(weekBookings, prevStart, prevEnd));
      cursorStart = prevStart;

      if (weekBookings.length > 0) { foundAny = true; break; }
    }

    if (!foundAny && allNewDays.length > 0) {
      const spanStart = cursorStart;
      const spanEnd = allNewDays[allNewDays.length - 1].date;
      const wixItems = await fetchFromWixLight(spanStart, spanEnd).catch(() => [] as BookingItem[]);
      if (wixItems.length > 0) {
        allNewDays.splice(0, allNewDays.length, ...groupBookingsByDay(wixItems, spanStart, spanEnd));
      }
    }

    lastLoadedPrevStartRef.current = cursorStart.toDateString();
    setBookingsByDay(prev => {
      // keep it sorted and free of duplicate days
      const have = new Set(prev.map(d => d.date.toDateString()));
      const fresh = allNewDays.filter(d => !have.has(d.date.toDateString()));
      return [...fresh, ...prev].sort((a, b) => a.date.getTime() - b.date.getTime());
    });
  }, [agendaRun, fetchWeekBookings, fetchFromWixLight]);

  // Create a refresh function for the Agenda component
  const handleAgendaRefresh = useCallback(async () => {

    // Clear all caches and unsubscribe existing listener
    firestoreUnsubRef.current?.();
    firestoreUnsubRef.current = null;
    bookingsCache.current.clear();
    sessionStorage.removeItem('cachedServiceIds');
    lastFetchedWindowRef.current = null;
    lastProcessedDateRef.current = null;

    // Force reload of current data
    setIsLoadingBookings(true);

    // Re-fetch data by calling the internal fetch function
    const windowStart = startOfDay(addDays(selectedDate, -7));
    const windowEnd = startOfDay(addDays(selectedDate, 7));
    windowStart.setHours(0, 0, 0, 0);
    windowEnd.setHours(23, 59, 59, 999);

    try {
      const today = new Date();
      // Past bounded at 90 days, future effectively unbounded (10 years) — see fetchBookingsForWindow's top-level comment.
      const fsWindowStart = new Date(today); fsWindowStart.setDate(today.getDate() - 90);
      const fsWindowEnd = new Date(today); fsWindowEnd.setDate(today.getDate() + 3650);
      const withinFirestoreWindow = windowStart >= fsWindowStart && windowEnd <= fsWindowEnd;

      let bookingsData: BookingItem[];
      if (withinFirestoreWindow) {
        const [firestoreBookings, firestoreBlocked] = await Promise.all([
          getBookingsFromCache(windowStart, windowEnd),
          getBlockedSlotsFromCache(windowStart, windowEnd),
        ]);
        const seen = new Set<string>();
        bookingsData = [];
        [...firestoreBookings, ...firestoreBlocked].forEach((item) => {
          if (seen.has(item.id)) return;
          seen.add(item.id);
          bookingsData.push(item);
        });
      } else {
        const service = DataServiceFactory.getService();
        const serviceIds = await loadServiceIds(service);
        bookingsData = await service.bookings.getBookingsAndBlockedSlotsByDateRange(windowStart, windowEnd, serviceIds);
      }

      // Cache the fresh data
      const cacheKey = `${windowStart.toDateString()}_${windowEnd.toDateString()}`;
      bookingsCache.current.set(cacheKey, {
        data: bookingsData,
        timestamp: Date.now()
      });

      // Update the UI
      const grouped = groupBookingsByDay(bookingsData, windowStart, windowEnd);
      setBookingsByDay(grouped);
      lastFetchedWindowRef.current = {
        start: windowStart.toDateString(),
        end: windowEnd.toDateString()
      };

    } catch (error) {
      console.error('❌ CALENDAR: Manual agenda refresh failed:', error);
    } finally {
      setIsLoadingBookings(false);
    }
  }, [selectedDate]);

  // Storage listener for navigation triggers from modals
  useEffect(() => {
    const removeListener = DataStorageService.setupStorageListener((key) => {
      if (key === 'navigate-to-contact') {
        const contactId = DataStorageService.getData<string>('navigate-to-contact');
        if (contactId) {
          DataStorageService.removeData('navigate-to-contact');
          keepStateOnReturnRef.current = true; // opened from an item: keep the page as it is
          ionRouter.push(`/tabs/contacts/${contactId}`);
        }
      } else if (key === 'navigate-to-inbox') {
        DataStorageService.removeData('navigate-to-inbox');
        keepStateOnReturnRef.current = true;
        ionRouter.push('/dashboard/team-messaging');
      } else if (key === 'open-booking-modal') {
        const bookingId = DataStorageService.getData<string>('open-booking-modal');
        console.log('Calendar: open-booking-modal event:', {
          bookingId,
          isBookingModalOpenRef: isBookingModalOpenRef.current,
          selectedBookingIdRef: selectedBookingIdRef.current
        });
        // Only open if no modal is currently open, or if it's a new booking (allow stacking)
        if (bookingId && !isBookingModalOpenRef.current && (!selectedBookingIdRef.current || bookingId === 'new')) {
          DataStorageService.removeData('open-booking-modal');
          console.log('Calendar: Opening booking modal from storage event');
          setSelectedBookingId(bookingId);
          setIsBookingModalOpen(true);
        } else if (bookingId && bookingId === 'new') {
          // Allow stacking for new bookings
          DataStorageService.removeData('open-booking-modal');
          console.log('Calendar: Opening new booking modal (stacked)');
          setSelectedBookingId(bookingId);
          setIsBookingModalOpen(true);
        } else if (bookingId) {
          console.log('Calendar: Modal already open, clearing storage key');
          DataStorageService.removeData('open-booking-modal');
        }
      } else if (key === 'edit-booking-modal') {
        const bookingId = DataStorageService.getData<string>('edit-booking-modal');
        if (bookingId) {
          DataStorageService.removeData('edit-booking-modal');
          console.log('Calendar: Opening edit booking modal');
          setSelectedBookingId(bookingId);  // Keep BookingDetails open
          setIsBookingModalOpen(true);      // Open BookingForm
        }
      } else if (key.startsWith('navigate-to-booking-')) {
        const bookingId = DataStorageService.getData<string>(key);
        DataStorageService.removeData(key);
        if (bookingId) {
          keepStateOnReturnRef.current = true;
          ionRouter.push(`/tabs/booking/${bookingId}`);
        }
      }
    });

    return removeListener;
  }, [ionRouter]);

  // Prevent browser back button from navigating when booking modal is open
  useEffect(() => {
    if (!isBookingModalOpen && !selectedBookingId) return;

    let isHandlingPopState = false;

    const handlePopState = (e: PopStateEvent) => {
      // Prevent multiple rapid popstate events
      if (isHandlingPopState) return;

      try {
        isHandlingPopState = true;

        // Close modals in reverse order (top to bottom)
        if (isBookingModalOpen) {
          // Close the booking modal first (it's on top)
          e.preventDefault();
          setIsBookingModalOpen(false);
        } else if (selectedBookingId) {
          // Close the booking details modal if no booking modal is open
          e.preventDefault();
          setSelectedBookingId(null);
        }
      } catch (error) {
        console.error('Error handling popstate event:', error);
      } finally {
        // Reset flag after a short delay to handle rapid presses
        setTimeout(() => {
          isHandlingPopState = false;
        }, 100);
      }
    };

    window.addEventListener('popstate', handlePopState, { passive: false });

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isBookingModalOpen, selectedBookingId]);

  return (
    <IonPage data-pageid="tabs-bookingCalendar-Calendar" className={styles.page}>
      {/* Portal target for the calendar-view PortalDropdown below — kept as a
          direct IonPage child (fixed, inset:0) rather than nested inside
          IonButtons, which doesn't reliably render/size a plain non-IonButton
          child slotted alongside real IonButtons. */}
      <div ref={calendarViewPortalRef} className={styles.pickerPortalRoot} />
      <IonHeader className='ion-no-border'>
        <IonToolbar className={styles.toolbarHeader}>
          <IonButtons slot="start">
            <IonBackButton defaultHref='/tabs/dashboard' type="button" text="" icon={chevronBack} />
            <IonButton
              shape="round"
              disabled
              style={{ visibility: 'hidden', pointerEvents: 'none' }}
            ></IonButton>
          </IonButtons>
          <IonTitle size="small">{tr('pages.bookingCalendar.Calendar.schedule')}</IonTitle>
          <IonButtons slot="end">
            <IonButton
              className={styles.circleButton}
              aria-label={tr('pages.bookingCalendar.Calendar.goToToday')}
              onClick={() => {
                const today = new Date();
                const newDate = startOfDay(today);
                const newMonth = new Date(newDate.getFullYear(), newDate.getMonth(), 1);

                // Set loading immediately to show skeleton instead of "no bookings" —
                // unless this window was already shown, then its days are still here.
                if (!loadedWindowKeysRef.current.has(windowKeyFor(newDate))) setIsLoadingBookings(true);

                // Clear the processed date ref to ensure data fetching runs
                lastProcessedDateRef.current = null;

                isAutoScrollingRef.current = true;
                setSelectedDate(newDate);
                setCurrentMonth(newMonth);
                setAgendaTopToken(t => t + 1);
                updateHeight(false);

                setTimeout(() => {
                  isAutoScrollingRef.current = false;
                }, 500);
              }}
            >
              <TodayCalendarIcon size={HEADER_ICON_SIZE} className={styles.todayIcon} />
            </IonButton>
            <IonButton
              ref={calendarViewTriggerRef}
              className={`${styles.circleButton} ${isViewPopoverOpen ? styles.active : ''}`}
              aria-label={tr('pages.bookingCalendar.Calendar.changeCalendarView')}
              onClick={() => setIsViewPopoverOpen(v => !v)}
            >
              {renderViewIcon()}
            </IonButton>
            <PortalDropdown
              isOpen={isViewPopoverOpen}
              anchorRef={calendarViewTriggerRef}
              portalRef={calendarViewPortalRef}
              onClose={() => setIsViewPopoverOpen(false)}
              align="end"
              title={tr('pages.bookingCalendar.Calendar.calendarView')}
              compact
              options={[
                {
                  value: 'agenda',
                  label: tr('pages.bookingCalendar.Calendar.agenda'),
                  icon: agendaIcon,
                  color: calendarView === 'agenda' ? 'primary' : 'medium',
                  selected: calendarView === 'agenda',
                  handler: () => handleViewChange('agenda'),
                },
                {
                  value: '3day',
                  label: tr('pages.bookingCalendar.Calendar.3Day'),
                  icon: <TbCalendarMonth size="1.35em" />,
                  color: calendarView === '3day' ? 'primary' : 'medium',
                  selected: calendarView === '3day',
                  handler: () => handleViewChange('3day'),
                },
                {
                  value: 'day',
                  label: tr('pages.bookingCalendar.Calendar.day'),
                  // size scales the SVG up from the row's 18px so it matches the
                  // Ionicons rows visually (TbCalendar has more built-in padding).
                  icon: <TbCalendar size="1.35em" />,
                  color: calendarView === 'day' ? 'primary' : 'medium',
                  selected: calendarView === 'day',
                  handler: () => handleViewChange('day'),
                },
              ]}
            />
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent scrollY={false} className={styles.pageContent}>
        <IonGrid className={styles.bookingContainer} fixed={true}>

          {calendarView === '3day' ? (
            <ThreeDayView
              key={viewResetToken}
              currentDate={selectedDate}
              bookings={threeDayBookings}
              onBookingSelect={handleBookingSelect}
              onDateChange={handleThreeDayDateSelection}
              completionStatuses={completionStatuses}
              onStatusChange={updateBookingStatus}
              onMonthClick={openMonthPicker}
              monthButtonRef={monthButtonRef}
            />
          ) : calendarView === 'day' ? (
            <>
              <IonCard className={styles.calendar}>
                <IonCardHeader className={styles.calendarNavHeader}>
                  <IonToolbar>
                    <IonTitle className={styles.monthTitle}>
                      {renderMonthButton()}
                    </IonTitle>

                    <IonButtons slot="end">
                      <IonButton
                        fill="clear"
                        aria-label={tr('pages.bookingCalendar.Calendar.previousPeriod')}
                        onClick={() => {
                          if (isExpanded) {
                            handleHorizontalScroll('left');
                          } else {
                            const newDate = addWeeks(selectedDate, -1);
                            handleDateSelection(newDate);
                          }
                        }}
                      >
                        <IonIcon icon={chevronBack} />
                      </IonButton>

                      <IonButton
                        fill="clear"
                        aria-label={tr('pages.bookingCalendar.Calendar.nextPeriod')}
                        onClick={() => {
                          if (isExpanded) {
                            handleHorizontalScroll('right');
                          } else {
                            const newDate = addWeeks(selectedDate, 1);
                            handleDateSelection(newDate);
                          }
                        }}
                      >
                        <IonIcon icon={chevronForward} />
                      </IonButton>
                    </IonButtons>
                  </IonToolbar>
                </IonCardHeader>

                <IonGrid className={styles.weekDays}>
                  <IonRow>
                    {weekdayNames('short').map(day => (
                      <IonCol key={day} className={styles.weekDay}>{day}</IonCol>
                    ))}
                  </IonRow>
                </IonGrid>
                <IonCard
                  ref={containerRef}
                  className={styles.calendarWrapper}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  <motion.div
                    className={`${styles.monthSlider} ${isExpanded ? styles.expanded : ''}`}
                    style={{
                      transform: xTransform,
                      height,
                      overflow: 'hidden'
                    }}
                  >
                    {!isExpanded ? (
                      <>
                        {renderWeekView(addWeeks(selectedDate, -1))}
                        {renderWeekView(selectedDate)}
                        {renderWeekView(addWeeks(selectedDate, 1))}
                      </>
                    ) : (
                      <>
                        <IonCol className={styles.monthViewWidth}>
                          {renderMonth(prevMonth, new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                        </IonCol>
                        <IonCol className={styles.monthViewWidth}>
                          {renderMonth(displayedDays, currentMonth)}
                        </IonCol>
                        <IonCol className={styles.monthViewWidth}>
                          {renderMonth(nextMonth, new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                        </IonCol>
                      </>
                    )}
                  </motion.div>
                </IonCard>

                <IonItem lines="none" className={styles.dragArea}
                  onTouchStart={handleVerticalDragStart}
                  onTouchMove={handleVerticalDragMove}
                  onTouchEnd={handleVerticalDragEnd}
                >
                  <IonLabel className={styles.dragHandleContainer}>
                    <IonProgressBar className={styles.dragHandle} value={0} />
                  </IonLabel>
                </IonItem>
              </IonCard>

              <DayView
                key={viewResetToken}
                currentDate={selectedDate}
                bookings={bookingsByDay.flatMap(day => day.items)}
                onBookingSelect={handleBookingSelect}
                completionStatuses={completionStatuses}
                onDateChange={(date: Date) => {
                  if (!isAutoScrollingRef.current && !touchState.current.isNavigating) {
                    if (!isSameDay(selectedDate, date)) {
                      setSelectedDate(new Date(date));
                      const newMonth = new Date(date.getFullYear(), date.getMonth(), 1);
                      if (currentMonth.getMonth() !== date.getMonth() ||
                        currentMonth.getFullYear() !== date.getFullYear()) {
                        setCurrentMonth(newMonth);
                      }
                    }
                  }
                }}
              />
            </>
          ) : (
            <>
              <IonCard className={styles.calendar}>
                <IonCardHeader className={styles.calendarNavHeader}>
                  <IonToolbar className="calendar-nav-toolbar">
                    <IonTitle className={styles.monthTitle}>
                      {renderMonthButton()}
                    </IonTitle>

                    <IonButtons slot="end">
                      <IonButton
                        fill="clear"
                        shape='round'
                        aria-label={tr('pages.bookingCalendar.Calendar.previousPeriod')}
                        onClick={() => {
                          if (isExpanded) {
                            handleHorizontalScroll('left');
                          } else {
                            const newDate = addWeeks(selectedDate, -1);
                            handleDateSelection(newDate);
                          }
                        }}
                      >
                        <IonIcon icon={chevronBack} />
                      </IonButton>

                      <IonButton
                        fill="clear"
                        shape='round'
                        aria-label={tr('pages.bookingCalendar.Calendar.nextPeriod')}
                        onClick={() => {
                          if (isExpanded) {
                            handleHorizontalScroll('right');
                          } else {
                            const newDate = addWeeks(selectedDate, 1);
                            handleDateSelection(newDate);
                          }
                        }}
                      >
                        <IonIcon icon={chevronForward} />
                      </IonButton>
                    </IonButtons>
                  </IonToolbar>
                </IonCardHeader>

                <IonGrid className={styles.weekDays}>
                  <IonRow>
                    {weekdayNames('short').map(day => (
                      <IonCol key={day} className={styles.weekDay}>{day}</IonCol>
                    ))}
                  </IonRow>
                </IonGrid>
                <IonCard

                  ref={containerRef}
                  className={styles.calendarWrapper}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  <motion.div
                    key={`calendar-${selectedDate.getTime()}`}
                    className={`${styles.monthSlider} ${isExpanded ? styles.expanded : ''}`}
                    style={{
                      transform: xTransform,
                      height,
                      overflow: 'hidden'
                    }}
                  >
                    {!isExpanded ? (
                      <>
                        {renderWeekView(addWeeks(selectedDate, -1))}
                        {renderWeekView(selectedDate)}
                        {renderWeekView(addWeeks(selectedDate, 1))}
                      </>
                    ) : (
                      <>
                        <IonCol className={styles.monthViewWidth}>
                          {renderMonth(prevMonth, new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                        </IonCol>
                        <IonCol className={styles.monthViewWidth}>
                          {renderMonth(displayedDays, currentMonth)}
                        </IonCol>
                        <IonCol className={styles.monthViewWidth}>
                          {renderMonth(nextMonth, new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                        </IonCol>
                      </>
                    )}
                  </motion.div>
                </IonCard>

                <IonItem lines="none" className={styles.dragArea}
                  onTouchStart={handleVerticalDragStart}
                  onTouchMove={handleVerticalDragMove}
                  onTouchEnd={handleVerticalDragEnd}
                >
                  <IonLabel className={styles.dragHandleContainer}>
                    <IonProgressBar className={styles.dragHandle} value={0} />
                  </IonLabel>
                </IonItem>
              </IonCard>

              <Agenda
                key={viewResetToken}
                selectedDate={selectedDate}
                scrollToTopToken={agendaTopToken}
                isCalendarExpanded={isExpanded}
                onDateSelect={handleWeekChange}
                bookings={agendaRun.flatMap(day => day.items)}
                serviceTypes={Object.keys(serviceColorMap) as Array<keyof typeof serviceColorMap>}
                onBookingSelect={handleBookingSelect}
                completionStatuses={completionStatuses}
                onStatusChange={updateBookingStatus}
                isLoading={isLoadingBookings}
                onLoadNextDays={loadNext7Days}
                onLoadPreviousDays={loadPrevious7Days}
                isAutoScrollingRef={isAutoScrollingRef}
                onRefresh={handleAgendaRefresh}
                triggerNavigationSkeletonRef={triggerNavigationSkeletonRef}
                onDateChange={(date: Date) => {
                  if (!isAutoScrollingRef.current && !touchState.current.isNavigating) {
                    if (!isSameDay(selectedDate, date)) {
                      // The date moved because the user scrolled the list: refetching the
                      // window for it must not swap the list for the loading skeleton
                      // (that unmounts the list and throws the scroll position away).
                      selectionFromAgendaScrollRef.current = true;
                      setSelectedDate(new Date(date));

                      const newMonth = new Date(date.getFullYear(), date.getMonth(), 1);
                      if (currentMonth.getMonth() !== date.getMonth() ||
                        currentMonth.getFullYear() !== date.getFullYear()) {
                        setCurrentMonth(newMonth);
                      }
                    }
                  }
                }}
              />
            </>
          )}
        </IonGrid>
      </IonContent>

      <IonButtons className={styles.splitFab}>
        <IonButton className={styles.splitFabHalf} aria-label={tr('pages.bookingCalendar.Calendar.addNewAppointment')} onClick={() => handleAddBooking()}>
          <LuCalendarPlus className={styles.addIcon} />
        </IonButton>
        <IonButton className={`${styles.splitFabHalf} ${styles.splitFabHalfBlocked}`} aria-label={tr('pages.bookingCalendar.Calendar.addNewBlockedTime')} onClick={() => handleAddBlockedTime()}>
          <LuCalendarX className={styles.addIcon} />
        </IonButton>
      </IonButtons>

      <ContactPickerSheet
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        onSelectContact={handleContactSelect}
      />

      <Booking
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        bookingId={selectedBookingId || undefined}
      />

      <BookingDetails
        isOpen={!!selectedBookingId && calendarView !== 'agenda'}
        onClose={() => setSelectedBookingId(null)}
        bookingId={selectedBookingId || undefined}
      />

      <MonthYearPickerModal
        isOpen={isMonthPickerOpen}
        onClose={() => setIsMonthPickerOpen(false)}
        anchorRef={monthButtonRef}
        // The 3-day view's month follows the selected date; the Day view's is currentMonth.
        currentMonth={
          calendarView === '3day'
            ? new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1)
            : currentMonth
        }
        // Jump the calendar to the picked month. Mirrors the "Go to today" button: show
        // the skeleton, clear the processed-date cache so data is refetched, then move
        // both the selected day and the visible month. Lands on today when the chosen
        // month is the current one, otherwise on the 1st. (Inline, like the other
        // handlers here — the React lint rule rejects a shared function that mutates
        // these refs once it's passed to a child component.)
        onSelectMonth={(year, month) => {
          const now = new Date();
          const isThisMonth = year === now.getFullYear() && month === now.getMonth();
          const newDate = startOfDay(isThisMonth ? now : new Date(year, month, 1));
          const newMonth = new Date(year, month, 1);

          setIsLoadingBookings(true);
          lastProcessedDateRef.current = null;

          isAutoScrollingRef.current = true;
          setSelectedDate(newDate);
          setCurrentMonth(newMonth);
          updateHeight(false);

          setTimeout(() => {
            isAutoScrollingRef.current = false;
          }, 500);
        }}
        // Tapping a day in the year view goes straight to that day (same refresh as
        // above). dayColors (the booked-days map the month grid's dots use) shades the days that
        // have bookings — only the loaded range has data.
        onSelectDate={date => {
          const newDate = startOfDay(date);
          const newMonth = new Date(newDate.getFullYear(), newDate.getMonth(), 1);

          setIsLoadingBookings(true);
          lastProcessedDateRef.current = null;

          isAutoScrollingRef.current = true;
          setSelectedDate(newDate);
          setCurrentMonth(newMonth);
          updateHeight(false);

          setTimeout(() => {
            isAutoScrollingRef.current = false;
          }, 500);
        }}
        dayColors={datesWithBookings}
      />

      <BlockedTimeForm
        isOpen={isBlockedFormOpen}
        onClose={handleBlockedFormClose}
        initialBooking={selectedBlockedBooking}
        initialDate={selectedDate}
        onSaved={handleBlockedFormSaved}
        onDeleted={handleBlockedFormSaved}
      />
    </IonPage>

  );
};

export default Calendar;
